// ===================================================================
// 👥 GERENCIADOR DE USUÁRIOS - IMPLEMENTAÇÃO COMPLETA
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { validateFields } from '../../utils/helpers';
import { validateNewPassword, PASSWORD_POLICY_HINT } from '../../utils/passwordPolicy';
import { useNotifications } from '../../contexts/NotificationContext';

const EyeToggle = ({ visible, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    tabIndex={-1}
    style={{
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      color: '#6B7280'
    }}
    title={visible ? 'Ocultar senha' : 'Ver senha'}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {visible ? (
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      ) : (
        <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
      )}
    </svg>
  </button>
);

export const UserManager = React.memo(({ user }) => {
  const [users, setUsers] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'analyst',
    password: '',
    confirmPassword: '',
    process_ids: []
  });
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showProcessList, setShowProcessList] = useState(false);
  const { addNotification } = useNotifications();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, processesData] = await Promise.all([
        apiClient.getUsers(),
        apiClient.getProcesses(true)
      ]);
      const all = Array.isArray(usersData) ? usersData : [];
      setUsers(all.filter(u => u.role !== 'candidate'));
      setProcesses(Array.isArray(processesData) ? processesData : []);
    } catch (err) {
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!showProcessList) return;
    const closeOnClickOutside = () => setShowProcessList(false);
    const t = setTimeout(() => document.addEventListener('click', closeOnClickOutside), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', closeOnClickOutside);
    };
  }, [showProcessList]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formError) setFormError('');
  }, [formError]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Para edição, validar apenas os campos obrigatórios (excluindo senha)
    const fieldsToValidate = editingUser 
      ? { name: formData.name, email: formData.email, role: formData.role }
      : formData;
    
    if (!validateFields(fieldsToValidate, ['name', 'email', 'role'])) {
      setFormError('Preencha todos os campos obrigatórios');
      return;
    }

    if (!editingUser) {
      const pwdErr = validateNewPassword(formData.password);
      if (pwdErr) {
        setFormError(pwdErr);
        return;
      }
    }

    if (!editingUser && formData.password !== formData.confirmPassword) {
      setFormError('As senhas não coincidem');
      return;
    }

    if (editingUser && formData.password) {
      const pwdErr = validateNewPassword(formData.password);
      if (pwdErr) {
        setFormError(pwdErr);
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setFormError('As senhas não coincidem');
        return;
      }
    }

    try {
      setLoading(true);
      
      if (editingUser) {
        const payload = { name: formData.name, email: formData.email, role: formData.role };
        if (formData.password) payload.password = formData.password;
        if (formData.role === 'analyst') payload.process_ids = formData.process_ids || [];
        const { error: updateError } = await apiClient.updateUser(editingUser.id, payload);
        if (updateError) {
          setFormError(updateError);
        } else {
          addNotification('Sucesso', 'Usuário atualizado com sucesso', 'success');
          setShowModal(false);
          setEditingUser(null);
          loadData();
        }
      } else {
        const { error: createError } = await apiClient.createUser({
          name: formData.name,
          email: formData.email,
          role: formData.role,
          password: formData.password,
          cpf: formData.cpf,
          process_ids: formData.role === 'analyst' ? (formData.process_ids || []) : []
        });
        if (createError) {
          setFormError(createError);
        } else {
          addNotification('Sucesso', 'Usuário criado com sucesso', 'success');
          setShowModal(false);
          loadData();
        }
      }
    } catch (err) {
      setFormError('Erro ao salvar usuário');
    } finally {
      setLoading(false);
    }
  }, [formData, editingUser, loadData, addNotification]);

  const handleEdit = useCallback((user) => {
    setEditingUser(user);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowProcessList(false);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      password: '',
      confirmPassword: '',
      process_ids: Array.isArray(user.process_ids) ? [...user.process_ids] : []
    });
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (userId) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) {
      return;
    }

    try {
      setLoading(true);
      const { error: deleteError } = await apiClient.deleteUser(userId);
      if (deleteError) {
        setError(deleteError);
      } else {
        addNotification('Sucesso', 'Usuário excluído com sucesso', 'success');
        loadData();
      }
    } catch (err) {
      setError('Erro ao excluir usuário');
    } finally {
      setLoading(false);
    }
  }, [loadData, addNotification]);

  const handleNewUser = useCallback(() => {
    setEditingUser(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowProcessList(false);
    setFormData({
      name: '',
      email: '',
      role: 'analyst',
      password: '',
      confirmPassword: '',
      process_ids: []
    });
    setShowModal(true);
  }, []);

  const toggleProcess = useCallback((processId) => {
    const id = typeof processId === 'number' ? processId : parseInt(processId, 10);
    setFormData(prev => {
      const ids = prev.process_ids || [];
      const has = ids.includes(id);
      return {
        ...prev,
        process_ids: has ? ids.filter(p => p !== id) : [...ids, id]
      };
    });
  }, []);

  if (loading && users.length === 0) {
    return <LoadingSpinner text="Carregando usuários..." />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>👥 Usuários (Admin e Analistas)</h2>
          <button
            onClick={handleNewUser}
            style={{
              ...styles.button,
              ...styles.buttonPrimary
            }}
          >
            ➕ Novo Admin/Analista
          </button>
        </div>

        {error && (
          <Alert 
            message={error} 
            type="error" 
            onClose={() => setError('')} 
          />
        )}

        {users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
            <h3>Nenhum admin ou analista cadastrado</h3>
            <p>Adicione administradores e analistas para gerenciar o sistema</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Nome</th>
                  <th style={styles.tableHeaderCell}>Email</th>
                  <th style={styles.tableHeaderCell}>Papel</th>
                  <th style={styles.tableHeaderCell}>Processos (analista)</th>
                  <th style={styles.tableHeaderCell}>Status</th>
                  <th style={styles.tableHeaderCell}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map(userItem => (
                  <tr key={userItem.id} style={styles.tableRow}>
                    <td style={styles.tableCell}>
                      <div style={{ fontWeight: '600', color: '#1F2937' }}>
                        {userItem.name}
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      {userItem.email}
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{
                        ...styles.statusBadge,
                        ...(userItem.role === 'admin' ? styles.statusRejected : 
                             userItem.role === 'analyst' ? styles.statusPending : 
                             userItem.role === 'campus' ? { ...styles.statusApproved, backgroundColor: '#D1FAE5', color: '#065F46' } :
                             styles.statusApproved)
                      }}>
                        {userItem.role === 'admin' ? 'Administrador' : 
                         userItem.role === 'analyst' ? 'Analista' : 
                         userItem.role === 'campus' ? 'Unidade' : 'Candidato'}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      {userItem.role === 'analyst' ? (
                        (userItem.process_ids || []).length > 0
                          ? (userItem.process_ids || []).map(pid => processes.find(p => p.id === pid)?.name).filter(Boolean).join(', ') || '—'
                          : <span style={{ color: '#6B7280', fontStyle: 'italic' }}>Todos</span>
                      ) : '—'}
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{
                        ...styles.statusBadge,
                        ...styles.statusApproved
                      }}>
                        Ativo
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleEdit(userItem)}
                          style={{
                            ...styles.button,
                            ...styles.buttonSecondary,
                            ...styles.buttonSmall
                          }}
                        >
                          ✏️ Editar
                        </button>
                        {userItem.id !== user.id && (
                          <button
                            onClick={() => handleDelete(userItem.id)}
                            style={{
                              ...styles.button,
                              ...styles.buttonDanger,
                              ...styles.buttonSmall
                            }}
                          >
                            🗑️ Excluir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setShowPassword(false); setShowConfirmPassword(false); setShowProcessList(false); setFormError(''); }}
        title={editingUser ? 'Editar Usuário' : 'Novo Admin/Analista'}
      >
        <form onSubmit={handleSubmit} style={styles.form}>
          {formError && (
            <div style={{ marginBottom: '16px' }}>
              <Alert
                message={formError}
                type="error"
                onClose={() => setFormError('')}
              />
            </div>
          )}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Nome Completo *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                style={styles.input}
                placeholder="Nome completo do usuário"
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                style={styles.input}
                placeholder="usuario@email.com"
                required
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Papel *</label>
              <select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                style={styles.select}
                required
              >
                <option value="analyst">Analista</option>
                <option value="admin">Administrador</option>
                <option value="campus">Unidade (relatórios e painel)</option>
              </select>
            </div>
          </div>

          {formData.role === 'analyst' && (
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', position: 'relative' }}>
                <label style={styles.label}>Processos com acesso</label>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>
                  Clique para abrir a lista e marcar em quais processos este analista pode trabalhar. Se nenhum for selecionado, ele terá acesso a todos.
                </p>
                <button
                  type="button"
                  onClick={() => setShowProcessList(prev => !prev)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: '400px',
                    padding: '10px 14px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151',
                    textAlign: 'left'
                  }}
                >
                  <span>
                    {(formData.process_ids || []).length === 0
                      ? 'Nenhum processo selecionado (acesso a todos)'
                      : `${(formData.process_ids || []).length} processo(s) selecionado(s)`}
                  </span>
                  <span style={{ fontSize: '10px', marginLeft: '8px' }}>{showProcessList ? '▲' : '▼'}</span>
                </button>
                {showProcessList && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '4px',
                      minWidth: '320px',
                      maxHeight: '240px',
                      overflowY: 'auto',
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      padding: '8px 0'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {processes.length === 0 ? (
                      <div style={{ padding: '12px 16px', color: '#6B7280', fontSize: '13px' }}>
                        Nenhum processo cadastrado
                      </div>
                    ) : (
                      processes.map(p => (
                        <label
                          key={p.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#374151',
                            backgroundColor: (formData.process_ids || []).includes(p.id) ? '#EFF6FF' : 'transparent'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={(formData.process_ids || []).includes(p.id)}
                            onChange={() => toggleProcess(p.id)}
                            style={{ margin: 0, width: '16px', height: '16px' }}
                          />
                          {p.name}
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {!editingUser ? (
            <>
              <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 12px', lineHeight: 1.5 }}>{PASSWORD_POLICY_HINT}</p>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Senha *</label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      style={{ ...styles.input, paddingRight: '40px', width: '100%' }}
                      placeholder="Mínimo 8 caracteres"
                      required
                    />
                    <EyeToggle visible={showPassword} onToggle={() => setShowPassword(v => !v)} />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Confirmar Senha *</label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      style={{ ...styles.input, paddingRight: '40px', width: '100%' }}
                      placeholder="Digite a senha novamente"
                      required
                    />
                    <EyeToggle visible={showConfirmPassword} onToggle={() => setShowConfirmPassword(v => !v)} />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 12px', lineHeight: 1.5 }}>{PASSWORD_POLICY_HINT}</p>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nova senha (opcional)</label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      style={{ ...styles.input, paddingRight: '40px', width: '100%' }}
                      placeholder="Deixe em branco para manter a atual"
                    />
                    <EyeToggle visible={showPassword} onToggle={() => setShowPassword(v => !v)} />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Confirmar nova senha</label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      style={{ ...styles.input, paddingRight: '40px', width: '100%' }}
                      placeholder="Repita a nova senha"
                    />
                    <EyeToggle visible={showConfirmPassword} onToggle={() => setShowConfirmPassword(v => !v)} />
                  </div>
                </div>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              style={{
                ...styles.button,
                ...styles.buttonSecondary
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.button,
                ...styles.buttonPrimary,
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Salvando...' : (editingUser ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}); 